import { useState, useCallback, useRef } from "react";
import { cn } from "../../lib/utils";
import { Sheet, SheetHeader, SheetBody } from "./Sheet";
import { ThemeToggle } from "./ThemeToggle";
import { downloadCSV, importCSVFile } from "../../lib/csvTasks";
import type { Card } from "../../data/types";
import type { CSVImportResult } from "../../lib/csvTasks";
import type { Theme, ResolvedTheme } from "../../hooks/useTheme";

interface SettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cards: Card[];
  onImportCards: (result: CSVImportResult) => Promise<{ created: number; skipped: number }>;
  onResetCards: () => Promise<number>;
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  onThemeChange: (theme: Theme) => void;
}

export function SettingsSheet({
  open,
  onOpenChange,
  cards,
  onImportCards,
  onResetCards,
  theme,
  resolvedTheme,
  onThemeChange,
}: SettingsSheetProps) {
  const [importStatus, setImportStatus] = useState<{
    type: "idle" | "success" | "error" | "loading";
    message: string;
  }>({ type: "idle", message: "" });

  const [hasExportedBackup, setHasExportedBackup] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleExport = useCallback(() => {
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadCSV(cards, `gtm-tasks-${timestamp}.csv`);
    setHasExportedBackup(true);
  }, [cards]);

  const handleImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportStatus({ type: "loading", message: "Parsing CSV…" });

    try {
      const result = await importCSVFile(file);

      if (result.errors.length > 0) {
        setImportStatus({
          type: "error",
          message: `${result.errors.length} error(s):\n${result.errors.slice(0, 5).join("\n")}${result.errors.length > 5 ? `\n… and ${result.errors.length - 5} more` : ""}`,
        });
        return;
      }

      if (result.cards.length === 0) {
        setImportStatus({
          type: "error",
          message: result.warnings.length > 0 ? result.warnings.join("\n") : "No valid cards found in CSV.",
        });
        return;
      }

      const { created, skipped } = await onImportCards(result);
      setImportStatus({
        type: "success",
        message: `Imported ${created} card(s).${skipped > 0 ? ` ${skipped} skipped (already exist).` : ""}${result.warnings.length > 0 ? `\n${result.warnings.join("\n")}` : ""}`,
      });
    } catch (err) {
      setImportStatus({
        type: "error",
        message: `Import failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      });
    }

    // Reset input so same file can be selected again
    if (fileRef.current) fileRef.current.value = "";
  }, [onImportCards]);

  const handleResetCards = useCallback(async () => {
    if (!hasExportedBackup) {
      const backupFirst = window.confirm(
        "⚠️ You haven't exported a backup yet this session.\n\nPlease export a backup CSV before resetting.\n\nClick OK to continue anyway, or Cancel to go back.",
      );
      if (!backupFirst) return;
    }

    const confirmed = window.confirm(
      "This will delete all cards from the platform. This action cannot be undone. Continue?",
    );
    if (!confirmed) return;

    setImportStatus({ type: "loading", message: "Resetting cards…" });

    try {
      const deleted = await onResetCards();
      setImportStatus({
        type: "success",
        message: `Reset completed. Deleted ${deleted} card(s). You can now import cards_master_v2.csv.`,
      });
    } catch (error) {
      setImportStatus({
        type: "error",
        message: `Reset failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
    }
  }, [onResetCards]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Settings">
      <SheetHeader onClose={() => onOpenChange(false)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-text-secondary)]">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">
          Settings
        </span>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-[var(--spacing-6)]">
          {/* Appearance */}
          <SettingsSection title="Appearance">
            <SettingsRow
              label="Theme"
              description="Switch between light, dark, or system preference"
            >
              <ThemeToggle
                theme={theme}
                resolved={resolvedTheme}
                onThemeChange={onThemeChange}
              />
            </SettingsRow>
          </SettingsSection>

          {/* General */}
          <SettingsSection title="General">
            <SettingsRow
              label="Team name"
              description="Your GTM team display name"
            >
              <span className="text-[14px] text-[var(--color-text-primary)]">AI Wave</span>
            </SettingsRow>
            <SettingsRow
              label="Default owner"
              description="Auto-mapped from signed-in account email"
            >
              <span className="text-[14px] text-[var(--color-text-primary)]">Auto</span>
            </SettingsRow>
          </SettingsSection>

          {/* Board */}
          <SettingsSection title="Board">
            <SettingsRow
              label="Show completed cards"
              description="Display done cards on the board"
            >
              <ToggleSwitch defaultChecked />
            </SettingsRow>
            <SettingsRow
              label="Auto-archive after"
              description="Days to auto-hide completed cards"
            >
              <span className="text-[14px] text-[var(--color-text-muted)]">Never</span>
            </SettingsRow>
          </SettingsSection>

          {/* Notifications */}
          <SettingsSection title="Notifications">
            <SettingsRow
              label="Due date reminders"
              description="Get notified before target dates"
            >
              <ToggleSwitch defaultChecked />
            </SettingsRow>
            <SettingsRow
              label="Status changes"
              description="Notify when a card status changes"
            >
              <ToggleSwitch />
            </SettingsRow>
          </SettingsSection>

          {/* CSV Import / Export */}
          <SettingsSection title="Data — CSV Import / Export">
            <div className="space-y-[var(--spacing-3)]">
              {/* Export */}
              <SettingsRow
                label={`Export ${cards.length} task(s)`}
                description="Download all board data as CSV"
              >
                <button
                  onClick={handleExport}
                  disabled={cards.length === 0}
                  className={cn(
                    "rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-1)]",
                    "text-[13px] font-medium",
                    "border bg-transparent cursor-pointer",
                    "transition-colors duration-[120ms]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                    cards.length > 0
                      ? "text-[var(--color-accent)] border-[var(--color-accent)] hover:bg-[var(--color-accent-subtle-bg)]"
                      : "text-[var(--color-text-muted)] border-[var(--color-border-stroke)] cursor-not-allowed",
                  )}
                >
                  ↓ Export CSV
                </button>
              </SettingsRow>

              {/* Import */}
              <SettingsRow
                label="Import from CSV"
                description="Add new tasks from a CSV file"
              >
                <label
                  className={cn(
                    "rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-1)]",
                    "text-[13px] font-medium text-[var(--color-accent)]",
                    "border border-[var(--color-accent)] bg-transparent",
                    "hover:bg-[var(--color-accent-subtle-bg)] transition-colors duration-[120ms]",
                    "cursor-pointer inline-block",
                    "focus-within:ring-2 focus-within:ring-[var(--color-accent-focus)]",
                  )}
                >
                  ↑ Import CSV
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleImport}
                    className="sr-only"
                  />
                </label>
              </SettingsRow>

              <SettingsRow
                label="Reset all cards"
                description={hasExportedBackup ? "Backup exported ✓ — safe to reset" : "⚠️ Export a backup first before resetting"}
              >
                <button
                  onClick={handleResetCards}
                  className={cn(
                    "rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-1)]",
                    "text-[13px] font-medium text-[var(--color-state-error)]",
                    "border border-[var(--color-state-error-border)] bg-transparent",
                    "hover:bg-[var(--color-state-error-bg)] transition-colors duration-[120ms]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                    "cursor-pointer",
                  )}
                >
                  Reset Cards
                </button>
              </SettingsRow>

              {/* Import status */}
              {importStatus.type !== "idle" && (
                <div className={cn(
                  "rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
                  "text-[13px] leading-[18px] whitespace-pre-wrap",
                  importStatus.type === "success" && "bg-[var(--color-state-success-bg)] text-[var(--color-state-success-text)] border border-[var(--color-state-success-border)]",
                  importStatus.type === "error" && "bg-[var(--color-state-error-bg)] text-[var(--color-state-error-text)] border border-[var(--color-state-error-border)]",
                  importStatus.type === "loading" && "bg-[var(--color-state-info-bg)] text-[var(--color-state-info-text)] border border-[var(--color-state-info-border)]",
                )}>
                  {importStatus.message}
                </div>
              )}

              {/* CSV Format Reference */}
              <CSVFormatReference />
            </div>
          </SettingsSection>

          {/* About */}
          <div className="pt-[var(--spacing-3)] border-t border-[var(--color-border-stroke)]">
            <p className="text-[12px] text-[var(--color-text-muted)]">
              AI Wave GTM Board · v1.0
            </p>
          </div>
        </div>
      </SheetBody>
    </Sheet>
  );
}

// ── CSV Format Reference (collapsible) ──────────────────────

function CSVFormatReference() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] overflow-hidden">
      <button
        onClick={() => setExpanded((p) => !p)}
        className={cn(
          "w-full flex items-center justify-between px-[var(--spacing-3)] py-[var(--spacing-2)]",
          "text-[13px] font-medium text-[var(--color-text-secondary)]",
          "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms]",
          "cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-focus)]",
        )}
      >
        <span>CSV Format Specification</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          className={cn("transition-transform duration-[120ms]", expanded && "rotate-180")}
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {expanded && (
        <div className="px-[var(--spacing-3)] pb-[var(--spacing-3)] text-[12px] leading-[18px] text-[var(--color-text-muted)] space-y-[var(--spacing-2)]">
          <div>
            <span className="font-semibold text-[var(--color-text-secondary)] block mb-[2px]">File rules</span>
            <ul className="list-disc pl-[16px] space-y-[1px]">
              <li>UTF-8 encoding (BOM optional)</li>
              <li>Comma (,) delimiter</li>
              <li>RFC 4180 quoting (double-quotes for fields with commas/newlines)</li>
              <li>Header row required — must match column names exactly</li>
            </ul>
          </div>

          <div>
            <span className="font-semibold text-[var(--color-text-secondary)] block mb-[2px]">Columns (exact order)</span>
            <div className="font-mono text-[11px] bg-[var(--color-bg-surface)] rounded-[var(--radius-sm)] p-[var(--spacing-2)] overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[var(--color-text-secondary)]">
                    <th className="pr-[8px] font-semibold">#</th>
                    <th className="pr-[8px] font-semibold">Column</th>
                    <th className="font-semibold">Allowed values</th>
                  </tr>
                </thead>
                <tbody>
                  {CSV_SPEC.map((col, i) => (
                    <tr key={col.name} className="border-t border-[var(--color-border-stroke)]">
                      <td className="pr-[8px] py-[2px] text-[var(--color-text-muted)]">{i + 1}</td>
                      <td className="pr-[8px] py-[2px] text-[var(--color-text-primary)]">{col.name}</td>
                      <td className="py-[2px]">{col.values}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <span className="font-semibold text-[var(--color-text-secondary)] block mb-[2px]">Key rules</span>
            <ul className="list-disc pl-[16px] space-y-[1px]">
              <li><strong>tags</strong> and <strong>dependsOn</strong> use semicolon (;) as separator</li>
              <li>GTM v2 tags expected: <strong>task:</strong>, <strong>phase:</strong>, <strong>window:</strong>, <strong>sla:</strong></li>
              <li>Dates must be <strong>YYYY-MM-DD</strong> (ISO 8601)</li>
              <li>Empty optional fields → leave blank (not "null")</li>
              <li>Duplicate IDs will be rejected</li>
              <li>Existing IDs on import are skipped (no overwrite)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

const CSV_SPEC = [
  { name: "id", values: "Unique string (required)" },
  { name: "laneId", values: "objective_kpis | offer_pricing | channels | commercial_pipeline | weekly_content | revops_tracking | feedback_loop" },
  { name: "title", values: "String, max 200 chars (required)" },
  { name: "description", values: "String or empty" },
  { name: "status", values: "planned | in_progress | blocked | done" },
  { name: "owner", values: "Joel | Albert | AI Wave | Joint" },
  { name: "priority", values: "critical | high | medium | low" },
  { name: "targetDate", values: "YYYY-MM-DD or empty" },
  { name: "completedDate", values: "YYYY-MM-DD or empty" },
  { name: "channel", values: "String or empty" },
  { name: "channelTier", values: "A | B | C or empty" },
  { name: "channelType", values: "owned | rented | borrowed or empty" },
  { name: "url", values: "URL string or empty" },
  { name: "utmCampaign", values: "String or empty" },
  { name: "tags", values: "Semicolon-separated or empty" },
  { name: "dependsOn", values: "Semicolon-separated IDs or empty" },
  { name: "notes", values: "String or empty" },
  { name: "weekNumber", values: "0 | 1 | 2 | 3 | 4 or empty" },
];

// ── Shared Components ───────────────────────────────────────

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[14px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.04em] mb-[var(--spacing-3)]">
        {title}
      </h3>
      <div className="space-y-[var(--spacing-1)]">
        {children}
      </div>
    </section>
  );
}

function SettingsRow({ label, description, children }: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(
      "flex items-center justify-between gap-[var(--spacing-4)]",
      "rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
      "hover:bg-[var(--color-bg-hover)] transition-colors duration-[120ms]",
    )}>
      <div className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium text-[var(--color-text-primary)]">{label}</span>
        <span className="block text-[12px] text-[var(--color-text-muted)] mt-[1px]">{description}</span>
      </div>
      <div className="shrink-0">
        {children}
      </div>
    </div>
  );
}

function ToggleSwitch({ defaultChecked = false }: { defaultChecked?: boolean }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" defaultChecked={defaultChecked} className="sr-only peer" />
      <div className={cn(
        "w-[36px] h-[20px] rounded-full transition-colors duration-[120ms]",
        "bg-[var(--color-bg-hover)] peer-checked:bg-[var(--color-accent)]",
        "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
        "after:bg-white after:rounded-full after:h-[16px] after:w-[16px]",
        "after:transition-transform after:duration-[120ms]",
        "peer-checked:after:translate-x-[16px]",
        "peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent-focus)] peer-focus-visible:ring-offset-2",
      )} />
    </label>
  );
}
